// One row per drone on the run. Drones join and finish at the scanner station,
// so this table reads; its only act is taking a part off a drone.

import React, { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../components/table/DataTable';
import { formatTimestamp } from '../utils/date-utils';
import Popover from './Popover';
import RemovalDialog from './RemovalDialog';
import { UnitStatusChip } from './WorkOrderBits';
import { buttonClass } from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import { Panel } from '../components/ui/Panel';
import Tooltip from '../components/ui/Tooltip';
import { COMPLETION_SOURCE_LABELS, formatCents, type Unit } from './workOrdersApi';

interface UnitMeta {
  canRemove: boolean;
  onRemove: (u: Unit) => void;
  lineNames: Record<number, string>;
}

const metaOf = (m: unknown) => m as UnitMeta;

const money = (key: 'materials_cents' | 'labor_cents' | 'overhead_cents' | 'unit_cost_cents', header: string) =>
  ({
    id: key.replace('_cents', ''),
    header,
    accessorFn: (u: Unit) => u[key] ?? undefined,
    meta: { align: 'right', numeric: true, csv: (u: Unit) => (u[key] == null ? '' : (u[key] as number) / 100) },
    cell: ({ row }) =>
      row.original[key] == null ? <span className="text-on-surface-variant">—</span> : formatCents(row.original[key]),
  }) as ColumnDef<Unit, any>;

/** The How cell: how a drone was finished, or why it was taken off the run. */
const howReading = (u: Unit): string | undefined => {
  if (u.status === 'flagged') return u.flag_reason || undefined;
  return u.completion_source ? COMPLETION_SOURCE_LABELS[u.completion_source] : undefined;
};

const COLUMNS: ColumnDef<Unit, any>[] = [
  {
    id: 'position',
    header: 'Unit',
    accessorFn: (u: Unit) => u.position,
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface-variant' },
  },
  {
    id: 'serial',
    header: 'Drone',
    accessorFn: (u: Unit) => u.serial ?? u.core_hex ?? undefined,
    meta: { cellClass: 'whitespace-nowrap' },
    cell: ({ row }) => {
      const u = row.original;
      if (u.serial) return <span className="font-mono text-on-surface">{u.serial}</span>;
      if (u.core_hex) return <span className="font-mono text-on-surface-variant">{u.core_hex}</span>;
      return <span className="text-on-surface-variant">—</span>;
    },
  },
  {
    id: 'status',
    header: 'Status',
    accessorFn: (u: Unit) => u.status,
    cell: ({ row }) => <UnitStatusChip status={row.original.status} />,
  },
  {
    id: 'completed',
    header: 'Built',
    accessorFn: (u: Unit) => u.completed_at ?? undefined,
    meta: { cellClass: 'whitespace-nowrap' },
    cell: ({ row }) => {
      const u = row.original;
      if (!u.completed_at) return <span className="text-on-surface-variant">—</span>;
      return (
        <div>
          <div className="tabular-nums text-on-surface">{formatTimestamp(u.completed_at, 'MMM D, h:mm A')}</div>
          {u.completed_by_name && <div className="text-xs text-on-surface-variant">{u.completed_by_name}</div>}
        </div>
      );
    },
  },
  {
    id: 'source',
    header: 'How',
    accessorFn: howReading,
    meta: { cellClass: 'max-w-[18rem] text-on-surface-variant' },
    cell: ({ row }) => {
      const how = howReading(row.original);
      if (!how) return '—';
      return (
        <span className="block truncate" title={how}>
          {how}
        </span>
      );
    },
  },
  {
    id: 'shortfall',
    header: 'Short',
    accessorFn: (u: Unit) => u.shortfall?.length || undefined,
    cell: ({ row, table }) => {
      const u = row.original;
      const m = metaOf(table.options.meta);
      if (!u.shortfall || u.shortfall.length === 0) return null;
      const n = u.shortfall.length;
      return (
        <Popover
          triggerLabel={`${n} short`}
          triggerAriaLabel={`Unit ${u.position}: ${n} ${n === 1 ? 'line' : 'lines'} short`}
          triggerClassName="inline-flex items-center whitespace-nowrap rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-300 hover:bg-amber-500/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          tooltip="Parts this drone was built without, or that were never scanned"
          title={`What unit ${u.position} was short`}
          width="md"
        >
          {() => (
            <ul className="space-y-1.5 text-sm">
              {u.shortfall!.map((s, i) => (
                <li key={`${s.line_id}-${i}`} className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 truncate text-on-surface">{m.lineNames[s.line_id] || s.target_key}</span>
                  <span className="whitespace-nowrap text-xs text-on-surface-variant">
                    {s.short ? `${s.short} short` : s.note === 'not scanned' ? 'Not scanned' : s.note || 'Short'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Popover>
      );
    },
  },
  money('materials_cents', 'Materials'),
  money('labor_cents', 'Labor'),
  money('overhead_cents', 'Overhead'),
  money('unit_cost_cents', 'Unit cost'),
  {
    id: 'actions',
    header: '',
    meta: { align: 'right' },
    cell: ({ row, table }) => {
      const u = row.original;
      const m = metaOf(table.options.meta);
      // A completed unit's cost is frozen; the server refuses the removal (unit_complete).
      if (!m.canRemove || !u.serial || u.status === 'complete') return null;
      return (
        <Tooltip label="Take a part off this drone and send it to stock, QC hold or scrap">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              m.onRemove(u);
            }}
            className={buttonClass('quiet', 'sm')}
          >
            Remove a part
          </button>
        </Tooltip>
      );
    },
  },
];

const getUnitId = (u: Unit) => u.id;

interface Props {
  orderId: string;
  units: Unit[];
  lineNames: Record<number, string>;
  /** The run's size, for the status line when `unitsToGo` did not arrive. */
  quantity: number;
  /** Drones the run still needs (`units_to_go`). */
  unitsToGo?: number;
  /** The order is not closed or cancelled, and the person may write. */
  canRemove: boolean;
  onChanged: () => void;
}

const UnitsSection: React.FC<Props> = ({
  orderId,
  units,
  lineNames,
  quantity,
  unitsToGo,
  canRemove,
  onChanged,
}) => {
  const [removing, setRemoving] = useState<Unit | null>(null);

  const meta = useMemo<UnitMeta>(
    () => ({ canRemove, onRemove: setRemoving, lineNames }),
    [canRemove, lineNames],
  );

  const hasMoney = units.some((u) => u.unit_cost_cents !== undefined);
  const columnVisibility = useMemo(
    () => ({
      materials: hasMoney,
      labor: hasMoney,
      overhead: hasMoney,
      unit_cost: hasMoney,
      actions: canRemove,
    }),
    [hasMoney, canRemove],
  );

  const built = units.filter((u) => u.status === 'complete').length;
  const flagged = units.filter((u) => u.status === 'flagged').length;
  const toGo = unitsToGo ?? Math.max(0, quantity - units.length + flagged);

  const strip = (
    <span className="text-xs tabular-nums text-on-surface-variant" role="status">
      {`${built} of ${quantity} built` + (toGo ? `, ${toGo} to go` : '') + (flagged ? `, ${flagged} flagged` : '')}
    </span>
  );

  return (
    <Panel title="Units" padding="none">
      <DataTable<Unit>
        data={units}
        columns={COLUMNS}
        meta={meta}
        getRowId={getUnitId}
        on="panel"
        columnVisibility={columnVisibility}
        sortable
        initialSorting={[{ id: 'position', desc: false }]}
        strip={strip}
        empty={<EmptyState variant="area">No drones on this run yet. A drone joins when Assembly mode scans it.</EmptyState>}
      />
      {removing && (
        <RemovalDialog
          orderId={orderId}
          unit={removing}
          onClose={() => setRemoving(null)}
          onDone={() => {
            setRemoving(null);
            onChanged();
          }}
        />
      )}
    </Panel>
  );
};

export default UnitsSection;
